import React, { useState } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, symbolFor, isIOwe } from "@/lib/format";

export default function RecordPaymentSheet({ open, onOpenChange, debt, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // holds updated debt when fully paid
  const iOwe = isIOwe(debt);

  const outstanding = debt?.outstanding_amount || 0;

  const reset = () => {
    setAmount("");
    setNote("");
    setDone(null);
  };

  const submit = async (confirmOverpay = false) => {
    const val = parseFloat(amount);
    if (!val || val <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/debts/${debt.id}/payments`, {
        amount: val,
        note: note || null,
        confirm_overpay: confirmOverpay,
      });
      if (data.status === "paid") {
        setDone(data);
      } else {
        toast.success(`${fmtNaira(val, debt.currency)} recorded. ${fmtNaira(data.outstanding_amount, debt.currency)} left.`);
        onDone?.(data);
        onOpenChange(false);
        reset();
      }
    } catch (e) {
      if (apiError(e) === "payment_exceeds_balance") {
        if (window.confirm(`That's more than the ${fmtNaira(outstanding, debt?.currency)} outstanding. Record it and mark this debt fully paid?`)) {
          return submit(true);
        }
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const closeDone = () => {
    onDone?.(done);
    onOpenChange(false);
    reset();
  };

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o && !done) reset(); onOpenChange(o); }}>
      <DrawerContent className="max-w-[30rem] mx-auto">
        {done ? (
          <div className="px-5 pb-8 pt-4 text-center animate-rise">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center animate-pop">
              <PartyPopper className="h-8 w-8 text-emerald-700" />
            </div>
            <h3 className="mt-4 font-display text-2xl font-bold text-foreground">All paid up ✓</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {iOwe
                ? `You've fully repaid ${fmtNaira(done.original_amount, debt.currency)} to ${debt.person_name}.`
                : `${debt.person_name} has fully repaid ${fmtNaira(done.original_amount, debt.currency)}.`}
            </p>
            <Button data-testid="payment-done-close" onClick={closeDone} className="mt-6 w-full h-12 rounded-xl text-base">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DrawerHeader className="text-left">
              <DrawerTitle className="font-display text-xl">Record payment</DrawerTitle>
            </DrawerHeader>
            <div className="px-5 pb-8 space-y-4">
              <div className="rounded-2xl bg-secondary p-4 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Outstanding</span>
                <span className="font-display text-2xl font-bold text-foreground tabular">{fmtNaira(outstanding, debt?.currency)}</span>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">{iOwe ? "How much did you pay?" : `How much did ${debt?.person_name} pay?`}</label>
                <div className="mt-2 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display text-2xl font-bold text-muted-foreground">{symbolFor(debt?.currency)}</span>
                  <Input
                    data-testid="payment-amount-input"
                    type="number"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                    className="h-16 pl-10 text-2xl font-display font-bold tabular rounded-2xl"
                  />
                </div>
              </div>
              <Button
                data-testid="payment-paid-in-full"
                variant="outline"
                onClick={() => setAmount(String(outstanding))}
                className="w-full h-11 rounded-xl border-primary/30 text-primary"
              >
                <CheckCircle2 className="h-4 w-4" /> Paid in full — {fmtNaira(outstanding, debt?.currency)}
              </Button>
              <Textarea
                data-testid="payment-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note (optional)"
                className="rounded-2xl resize-none"
                rows={2}
              />
              <Button
                data-testid="payment-submit"
                disabled={loading}
                onClick={() => submit(false)}
                className="w-full h-12 rounded-xl text-base"
              >
                {loading ? "Saving…" : "Record payment"}
              </Button>
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
